import { useTranslation } from 'react-i18next';
import Carousel from 'nuka-carousel';
import { useState, useContext } from 'react';
import { useMeasure } from 'react-use';
import css from 'styled-jsx/css';
import { Arrow, Directions } from 'components/Shared/Arrow';
import useEvents from 'lib/hooks/useEvents';
import scrollEvent, {
  Directions as ScrollDirections,
} from 'lib/Tracking/ListingsCarousel/ScrollEvent';
import VdpEventContext from 'contexts/VdpEventContext';
import { useRouter } from 'next/router';
import { SourcePages } from 'lib/EventSchema';
import styles from './styles';
import ListingCard from './ListingCard';
import { ListingViewModel } from './types';

interface ListingsCarouselProps {
  listings: Array<ListingViewModel>;
}

const CARD_WIDTH = 272;
const CARD_SPACING = 24;

const sliderCss = css.resolve`
  .listing-slider :global(.slider-control-bottomcenter) {
    display: none;
  }

  .listing-slider :global(.slider-control-centerleft) {
    display: none;
  }

  .listing-slider :global(.slider-control-centerright) {
    display: none;
  }

  .listing-slider :global(.slider-slide):focus {
    outline: 0;
  }
`;

const hasOverflow = (width: number, length: number): boolean => {
  return width < (CARD_WIDTH + CARD_SPACING) * length - CARD_SPACING;
};

const atEndOfCarousel = (index: number, width: number, length: number): boolean => {
  const slides = width / (CARD_WIDTH + CARD_SPACING);
  const averageCard = (slides * (CARD_WIDTH + CARD_SPACING) - CARD_SPACING) / slides;
  const numSlidesShown = Math.floor(width / averageCard);

  return index + numSlidesShown === length;
};

function ListingsCarousel({ listings }: ListingsCarouselProps): JSX.Element {
  const { t } = useTranslation();
  const logger = useEvents();
  const [slideIndex, setSlideIndex] = useState(0);
  const [listingCarouselRef, { width }] = useMeasure<HTMLDivElement>();
  const showLeftArrow: boolean = slideIndex !== 0;
  const showRightArrow =
    hasOverflow(width, listings.length) && !atEndOfCarousel(slideIndex, width, listings.length);

  const { trackingParams } = useContext(VdpEventContext);
  const { asPath } = useRouter();

  function logScrollEvent(direction: ScrollDirections): void {
    logger.log(scrollEvent({ direction, pathName: asPath as SourcePages, trackingParams }));
  }

  let slidesToShow = width > 0 ? Math.floor(width / CARD_WIDTH) : 1;
  if (slidesToShow === 1) {
    slidesToShow = 1.3;
  }

  return (
    <div
      className="listings-carousel"
      ref={listingCarouselRef}
      style={{ width: `${CARD_WIDTH}px` }}
    >
      <div className="header">{t('vdp.similarListings')}</div>
      {/* we need the heightMode prop from <v5 nuka-carousel to calculate the height of on page slideshow https://github.com/autolist/autolist-web/pull/4471 */}
      {/* eslint-disable-next-line @typescript-eslint/ban-ts-comment */}
      {/* @ts-ignore */}
      <Carousel
        slidesToShow={slidesToShow}
        className={`listing-slider ${sliderCss.className}`}
        slideIndex={slideIndex}
        afterSlide={setSlideIndex}
        renderTopLeftControls={({ previousSlide }): JSX.Element =>
          showLeftArrow ? (
            <Arrow
              alt={t('previous')}
              direction={Directions.Left}
              onClick={(): void => {
                logScrollEvent('bk');
                previousSlide();
              }}
              style={{ marginTop: '50px', marginLeft: '8px' }}
              data-testid="left-arrow"
            />
          ) : (
            <></>
          )
        }
        renderTopRightControls={({ nextSlide }): JSX.Element =>
          showRightArrow ? (
            <Arrow
              alt={t('next')}
              onClick={(): void => {
                logScrollEvent('fwd');
                nextSlide();
              }}
              direction={Directions.Right}
              style={{ marginTop: '50px', marginRight: '8px' }}
              data-testid="right-arrow"
            />
          ) : (
            <></>
          )
        }
        cellSpacing={CARD_SPACING}
      >
        {listings.map((listing) => (
          <ListingCard key={listing.vdpUrl} {...listing} />
        ))}
      </Carousel>
      <style jsx>{styles}</style>
      {sliderCss.styles}
    </div>
  );
}

export default ListingsCarousel;
